// @flow

import { without, noop, omit, map, extend } from 'lodash';

import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';
import decodeFriendRequest from '../normalizers/decodeFriendRequest';
import { THEY_REQUESTED } from '../constants/friendStatusTypes';
import {
  SET_NOTIFICATIONS_READ,
  CLEAR_NOTIFICATIONS,
  API_CALL_SUCCESS
} from '../constants/actionTypes';
import {
  FRIEND_REQUESTS,
  ACCEPT_FRIEND_REQUEST,
  REMOVE_FRIEND_REQUEST,
  USER_SIGN_OUT
} from '../constants/apiTypes';
import { PENDING_FRIEND_REQUEST } from '../constants/notificationTypes';

const initialState: NotificationCentreState = {
  // a map of type of notification => array of notifications for type
  notifications: {}
};

function removeRequestNotification(state: NotificationCentreState, action: { id: number }) {
  const requestNotifications = state.notifications[PENDING_FRIEND_REQUEST] || [];
  const pendingNotification = requestNotifications.find(
    n => n.type === PENDING_FRIEND_REQUEST && n.id === action.id
  );

  return {
    notifications: {
      ...state.notifications,
      [PENDING_FRIEND_REQUEST]: without(requestNotifications, pendingNotification)
    }
  };
}

function setNotifications(
  state: NotificationCentreState,
  type: string,
  notifications: Array<iNotification>
) {
  const typeNotifications = state.notifications[type] || [];
  const existingIds = new Set(map(typeNotifications, 'id'));
  const nextNotifications = notifications.map(not => {
    // if it didn't exist before, must be set, set the status
    if (!existingIds.has(not.id)) {
      return {
        ...not,
        status: 'NEW'
      };
    }
    // return the existing one from state so we keep the status
    // there's nothing else to change in theory
    return {
      ...typeNotifications.find(n => n.id === not.id),
      ...not
    };
  });

  return {
    notifications: {
      ...omit(state.notifications, type),
      [type]: nextNotifications
    }
  };
}

function fetchedRequests(state: NotificationCentreState, action) {
  const notifications = action.friendRequests
    .map(req => decodeFriendRequest(req))
    .filter(request => request.status === THEY_REQUESTED)
    .map(req => ({
      name: req.username,
      type: PENDING_FRIEND_REQUEST,
      id: req.id,
      data: req
    }));

  return setNotifications(state, PENDING_FRIEND_REQUEST, notifications);
}

const apiSuccessReducer = switchFunc(
  {
    [FRIEND_REQUESTS]: fetchedRequests,
    [ACCEPT_FRIEND_REQUEST]: removeRequestNotification,
    [REMOVE_FRIEND_REQUEST]: removeRequestNotification,
    [USER_SIGN_OUT]: () => initialState
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_SUCCESS]: (state, action) => ({
    ...apiSuccessReducer(action.api)(state, action)
  }),
  [SET_NOTIFICATIONS_READ]: (state: NotificationCentreState) => {
    const readNotifications = map(state.notifications, (notifications, type) => {
      const read = notifications.map(not => ({
        ...not,
        status: 'READ'
      }));

      return {
        [type]: read
      };
    });

    return {
      notifications: {
        ...extend({}, ...readNotifications)
      }
    };
  },
  [CLEAR_NOTIFICATIONS]: () => initialState
});
